// Routine panel in the task drawer — today's slots, one Tick / "I've caught up" button, and who ticked what.
import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Loader2, Repeat } from "lucide-react";
import { workspaceApi } from "../_shared";
import { tickRoutine } from "./TaskBits";

const CELL = { done: "bg-teal-400/80", caught_up: "bg-amber-300/80", missed: "bg-rose-500/70", due: "bg-violet-400 animate-pulse", upcoming: "bg-white/10" };
const ago = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit" });

export default function RoutinePanel({ task, onTicked }) {
  const r = task.routine;
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => workspaceApi.routineTicks(task.id).then((d) => setHist(d.items || [])).catch(() => {}), [task.id]);
  useEffect(() => { load(); }, [load, r?.done]);
  if (!r) return null;
  const tick = async () => { setBusy(true); await tickRoutine(task, async () => { const d = await workspaceApi.getTask(task.id); onTicked?.(d.task || d); load(); }); setBusy(false); };
  return (
    <div className="rounded-xl border border-violet-400/25 bg-violet-500/[0.05] p-3 space-y-3" data-testid="routine-panel">
      <div className="flex items-center gap-2">
        <Repeat className="w-4 h-4 text-violet-300" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] text-white font-medium">{r.label} routine{r.window ? ` · ${r.window}` : ""}{r.paused ? " · paused" : ""}</div>
          <div className="text-[11.5px] text-slate-400" data-testid="routine-summary">
            {r.total > 1 ? `${r.done}/${r.total} slots today${r.missed ? ` · ${r.missed} missed` : ""}${r.next_at ? ` · next ${r.next_at}` : ""}` : r.due_now ? "Due now" : r.done ? "Done for this period" : "Opens later"}
            {r.last_by && ` · last by ${r.last_by}`}
          </div>
        </div>
        {!r.paused && <button onClick={tick} disabled={busy || (!r.due_now && !r.missed)} data-testid="routine-tick-btn"
          className="px-3 py-1.5 rounded-lg bg-violet-500/25 border border-violet-300/50 text-violet-50 text-[12px] inline-flex items-center gap-1.5 hover:bg-violet-500/35 disabled:opacity-40 transition-colors">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}{r.missed ? "I've caught up" : r.due_now ? "Tick" : "Up to date"}</button>}
      </div>
      {r.slots?.length > 0 && (
        <div className="flex flex-wrap gap-1" data-testid="routine-slots">
          {r.slots.map((c) => <span key={c.key} title={`${c.start} · ${c.state.replace("_", " ")}${c.by ? ` · ${c.by}` : ""}`} className={`w-9 h-5 rounded ${CELL[c.state]} grid place-items-center text-[8.5px] font-mono text-slate-900`} data-testid={`routine-slot-${c.start.replace(":", "")}`}>{c.start}</span>)}
        </div>
      )}
      <div className="flex flex-wrap gap-3 text-[10.5px] text-slate-500">
        {[["done", "on time"], ["caught_up", "caught up"], ["missed", "missed"], ["due", "due now"]].map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-sm ${CELL[k]}`} />{l}</span>)}
      </div>
      {hist.length > 0 && (
        <div className="border-t border-white/[0.06] pt-2 space-y-1 max-h-36 overflow-y-auto" data-testid="routine-history">
          {hist.slice(0, 12).map((h) => <div key={h.id} className="text-[11.5px] text-slate-300 flex gap-2"><span className="text-slate-500 w-24 shrink-0">{ago(h.at)}</span><span className="truncate">{h.by_name}{h.covers > 1 ? ` · caught up ${h.covers} slots` : " ticked"}{h.note ? ` — ${h.note}` : ""}</span></div>)}
        </div>
      )}
    </div>
  );
}
